import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';
import dayjs from 'dayjs';

import calculateAgeFromBirthday from '../functions/calculateAgeFromBirthday';
import MessagePropType from '../definitions/MessagePropType';
import { MONTH_DAY_YEAR_FORMAT } from '../definitions/DateFormats';
import Question from './Question';


function QuestionDateOfBirth({
    name,
    value,
    dateFormat,
    minimumAge,
    minimumAgeValidationMessage,
    maximumAge,
    maximumAgeValidationMessage,
    labelMessage,
    required,
    ...otherProps
}) {
    return (
        <Question
            id="dob"
            field={{
                name,
                type: 'date',
                // TODO: adjust this to value: value once DateInput has been updated to accept ISO string date format
                value: value && dayjs(value).format(MONTH_DAY_YEAR_FORMAT),
                required,
                dateFormat: dateFormat,
                validators: [
                    {
                        fn: dayOfBirth => dayjs(dayOfBirth).startOf('day') <= dayjs().startOf('day'),
                        message: (
                            <FormattedMessage
                                id="cc.hosted-app.about-you.dateOfBirth.future"
                                defaultMessage="This date is from the future!"
                            />
                        ),
                    },
                    {
                        fn: dayOfBirth => calculateAgeFromBirthday(dayOfBirth) >= minimumAge,
                        message: minimumAgeValidationMessage ?? (
                            <FormattedMessage
                                id="cc.hosted-app.about-you.dateOfBirth.must-be-old-enough"
                                defaultMessage="You must be {minimumAge} years or older to apply."
                                values={{
                                    minimumAge,
                                }}
                            />
                        ),
                    },
                    {
                        fn: dayOfBirth => calculateAgeFromBirthday(dayOfBirth) <= maximumAge,
                        message: maximumAgeValidationMessage ?? (
                            <FormattedMessage
                                id="base-ui.date-of-birth.must-be-young-enough"
                                defaultMessage="You must be less than {maximumAge} years old to apply"
                                values={{
                                    maximumAge,
                                }}
                            />
                        ),
                    },
                ],
            }}
            labelMessage={labelMessage ?? (
                <FormattedMessage
                    id="cc.hosted-app.about-you.dateOfBirth.label"
                    defaultMessage="What is your date of birth?"
                />
            )}
            isSeparateInputs={true} // specific to date input
            {...otherProps}
        />
    );
}

QuestionDateOfBirth.propTypes = {
    name: PropTypes.string,
    value: PropTypes.string,
    dateFormat: PropTypes.string,
    minimumAge: PropTypes.number,
    minimumAgeValidationMessage: MessagePropType,
    maximumAge: PropTypes.number,
    maximumAgeValidationMessage: MessagePropType,
    labelMessage: MessagePropType,
    required: PropTypes.bool,
};

QuestionDateOfBirth.defaultProps = {
    name: 'dob',
    value: undefined,
    dateFormat: MONTH_DAY_YEAR_FORMAT,
    minimumAge: 16,
    minimumAgeValidationMessage: undefined,
    maximumAge: 100,
    maximumAgeValidationMessage: undefined,
    labelMessage: undefined,
    required: true,
};


export default observer(QuestionDateOfBirth);
